import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

/**
 * Context for document UI state management - handles selection, filters, etc.
 * Follows single responsibility principle by only managing UI state, not data
 */
interface DocumentUIContextType {
  // UI state
  selectedCategory: string | undefined
  setSelectedCategory: (category: string | undefined) => void

  // Future UI state can be added here:
  // searchQuery: string
  // sortOrder: 'asc' | 'desc'
  // viewMode: 'list' | 'grid'
}

const DocumentUIContext = createContext<DocumentUIContextType | undefined>(
  undefined
)

interface DocumentUIProviderProps {
  children: ReactNode
}

export const DocumentUIProvider: FC<DocumentUIProviderProps> = ({
  children,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    undefined
  )

  const handleSetSelectedCategory = useCallback(
    (category: string | undefined) => {
      setSelectedCategory(category)
    },
    []
  )

  const contextValue = useMemo<DocumentUIContextType>(
    () => ({
      selectedCategory,
      setSelectedCategory: handleSetSelectedCategory,
    }),
    [selectedCategory, handleSetSelectedCategory]
  )

  return (
    <DocumentUIContext.Provider value={contextValue}>
      {children}
    </DocumentUIContext.Provider>
  )
}

export const useDocumentUI = (): DocumentUIContextType => {
  const context = useContext(DocumentUIContext)
  if (context === undefined) {
    throw new Error('useDocumentUI must be used within a DocumentUIProvider')
  }
  return context
}
